import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { normalizeId } from "utils";
import { budgetKeys } from "queries";
import { useAxios } from "../useAxios";
import { unwrapBudgetApiData } from "./budgetCachePatch";

export interface BudgetVersionDiffSummary {
  base_budget_id: number;
  compare_budget_id: number;
  base_version_name: string | null;
  compare_version_name: string | null;
  summary: {
    added_count: number;
    removed_count: number;
    changed_count: number;
    before_grand_total: number;
    after_grand_total: number;
  };
  category_changes: Array<{
    category_name: string;
    status: string;
    added_count: number;
    removed_count: number;
    changed_count: number;
    before_total: number;
    after_total: number;
  }>;
}

interface UseBudgetVersionDiffParams {
  projectId: string | number;
  documentId: string | number;
  budgetId?: string;
  againstBudgetId?: number;
  enabled?: boolean;
}

export const useBudgetVersionDiff = ({
  projectId,
  documentId,
  budgetId,
  againstBudgetId,
  enabled = true,
}: UseBudgetVersionDiffParams) => {
  const axios = useAxios({ allResponse: false });
  const pid = normalizeId(projectId);
  const did = normalizeId(documentId);

  return useQuery({
    queryKey: [...budgetKeys.all, "version-diff", pid, did, budgetId, againstBudgetId ?? "primary"],
    queryFn: async (): Promise<BudgetVersionDiffSummary> => {
      if (!budgetId) throw new Error("Budget ID required");
      const params = againstBudgetId ? { against: againstBudgetId } : undefined;
      const response = await axios.get<
        BudgetVersionDiffSummary | { data: BudgetVersionDiffSummary }
      >(USER_API.budgetVersionDiff(pid, did, budgetId), { params });
      return unwrapBudgetApiData<BudgetVersionDiffSummary>(
        response as unknown as BudgetVersionDiffSummary | { data: BudgetVersionDiffSummary }
      );
    },
    enabled: enabled && Boolean(budgetId),
  });
};
